//uzywac tylko, jeśli to jest ostateczna opcja (np l10n)
export const getValueByPath = <T = unknown>(obj: unknown, path: string): T =>
	path?.split('.').reduce((pV, cV) =>
		(pV && typeof pV === 'object') ? (pV as Record<string, unknown>)[cV] : undefined,
		obj
	) as T;

export const omit = (obj: Record<string, unknown>, arr: string[]): Record<string, unknown> =>
	Object.keys(obj)
		.filter(k => !arr.includes(k))
		.reduce((acc, key) => {
			acc[key] = obj[key];
			return acc;
		}, {} as Record<string, unknown>);

export const pickBy = <T>(obj: Partial<T>, fn: (value: unknown, key: string, object: Partial<T>) => boolean): Partial<T> => {
	const source = obj as unknown as Record<string, unknown>;
	const result = {} as Record<string, unknown>;

	for (const key of Object.keys(source)) {
		const val = source[key];
		if (fn(val, key, obj)) {
			result[key] = val;
		}
	}

	return result as unknown as Partial<T>;
};

type MergeOptions = {
	arrayMerge?: (target: unknown[], source: unknown[]) => unknown[];
};

const isPlainObject = (item: unknown): item is Record<string, unknown> => {
	if (typeof item !== 'object' || item === null) {
		return false;
	}
	const prototype = Object.getPrototypeOf(item);
	return prototype === null || prototype === Object.prototype;
};

/**
* @description
* Deeply merges source object into target object.
* 
* @example
* ```typescript
* const target = { a: 1, b: { c: 2 } };
* const source = { b: { d: 3 }, e: 4 };
* 
* const result = mergeDeep(target, source); 
* result is { a: 1, b: { c: 2, d: 3 }, e: 4 }
* ```
*
* @param target The object to merge into.
* @param source The object to merge from.
* @param options Options for merging.
* @returns The merged object.
*/
export function mergeDeep<T>(target: T, source: Partial<T>, options?: MergeOptions): T {
	if (!isPlainObject(target)) {
		return target;
	}
	if (!isPlainObject(source)) {
		return { ...target };
	}

	const output = { ...target };

	for (const key of Object.keys(source)) {
		const targetVal = (target as Record<string, unknown>)[key];
		const sourceVal = (source as Record<string, unknown>)[key];

		if (Array.isArray(targetVal) && Array.isArray(sourceVal)) {
			(output as Record<string, unknown>)[key] = options?.arrayMerge
				? options.arrayMerge(targetVal, sourceVal)
				: [...targetVal, ...sourceVal];
		} else if (isPlainObject(targetVal) && isPlainObject(sourceVal)) {
			(output as Record<string, unknown>)[key] = mergeDeep(targetVal, sourceVal, options);
		} else {
			(output as Record<string, unknown>)[key] = sourceVal;
		}
	}

	return output as T;
}


/**
* @description
* Deeply merges multiple source objects into a target object.
* 
* @example
* ```typescript
* const target = { a: 1, b: { c: 2 } };
* const source1 = { b: { d: 3 }, e: 4 };
* const source2 = { b: { f: 5 }, g: 6 };
* 
* const result = mergeDeepAll([target, source1, source2]); 
* result is { a: 1, b: { c: 2, d: 3, f: 5 }, e: 4, g: 6 }
* ```
*
* @param objects Array of objects to be merged sequentially.
* @returns The merged object.
*/
export function mergeDeepAll<T>(objects: T[]): T {
	return objects.reduce((acc, obj) => mergeDeep(acc, obj as Partial<T>), {} as T);
}